import {
  UseMutateAsyncFunction,
  UseMutateFunction,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { BaseError } from '~/components/Error/BaseError';

export type UseSecurityOptions<
  TVariables extends unknown[] = unknown[],
  TError extends Error = BaseError,
> = UseMutationOptions<void, TError, TVariables | void> & {
  values: TVariables;
  validate: (variables?: TVariables) => Promise<string | void> | string | void;
  enabled?: boolean;
};
export type UseSecurityResult<
  TVariables extends unknown[] = unknown[],
  TError extends Error = BaseError,
> = UseMutationResult<void, TError, TVariables | void> & {
  values: TVariables;
  validate: UseMutateFunction<void, TError, void | TVariables>;
  validateAsync: UseMutateAsyncFunction<void, TError, TVariables | void>;
};
export const useSecurity = <
  TVariables extends unknown[] = unknown[],
  TError extends Error = BaseError,
>({
  values,
  validate,
  enabled = true,
  ...options
}: UseSecurityOptions<TVariables, TError>) => {
  const { mutate, mutateAsync, isError, ...rest } = useMutation<void, TError, TVariables | void>({
    mutationFn: async () => {
      const error = await validate(values);
      if (error) {
        throw new BaseError(error);
      }
      return;
    },
    onError: undefined,
    ...options,
  });

  useEffect(() => {
    if (!isError && enabled) {
      mutate(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...values, enabled]);

  return {
    values,
    validate: mutate,
    validateAsync: mutateAsync,
    mutate,
    mutateAsync,
    ...rest,
  } as UseSecurityResult<TVariables, TError>;
};

export const useTransition = <T = void>(): [
  boolean,
  (func: () => T | Promise<T>) => Promise<T>,
] => {
  const [loading, setLoading] = useState(false);
  return [
    loading,
    useCallback(async (func) => {
      setLoading(true);
      try {
        return await func();
      } finally {
        setLoading(false);
      }
    }, []),
  ];
};
